import { z } from 'zod'

import i18n from 'modules/core/lib/i18n'

import { FormContext } from 'modules/workflows/components/Sidepanel/schemas/nodeSidepanelSchema'
import { createTaskFormSchema } from 'modules/workflows/components/Sidepanel/schemas/taskFormSchema'

export const createDownloadDocumentHandlerSchema = (formContext: FormContext) => {
  const employeeSegmentSchema = z.union([
    z.object({
      name: z.enum(['anyone_in_team', 'team_leads'], {
        message: i18n.t('workflows.sidepanel.approval.validation.strategy_required'),
      }),
      input: z
        .object({
          team_id: z
            .number({
              message: i18n.t('workflows.sidepanel.approval.validation.team_required'),
            })
            .nullable(),
        })
        .refine(
          (data) => {
            return data.team_id && data.team_id > 0
          },
          {
            message: i18n.t('workflows.sidepanel.approval.validation.team_required'),
          }
        ),
    }),
    z.object({
      name: z.enum(['employee_manager', 'admins', 'resource_owner'], {
        message: i18n.t('workflows.sidepanel.approval.validation.strategy_required'),
      }),
      input: z.object({}),
    }),
    z.object({
      name: z.literal('job_catalog_role'),
      input: z.object({
        role_id: z.string(),
      }),
    }),
  ])

  return createTaskFormSchema(formContext).extend({
    nodeType: z.literal('task'),
    nodeData: z.object({
      handlerType: z.literal('download_document'),
      payload: z
        .object({
          title: z.string().min(1, {
            message: i18n.t('procurement.workflows.basic_task_handler.validation.title_required'),
          }),
          description: z.string().min(1, {
            message: i18n.t(
              'procurement.workflows.basic_task_handler.validation.description_required'
            ),
          }),
          employee_ids: z.array(z.number()).optional(),
          employee_segments: z.array(employeeSegmentSchema).optional(),
          fallback_employee_ids: z.array(z.number()).optional(),
          fallback_employee_segments: z.array(employeeSegmentSchema).optional(),
          // Files are stored as File objects in the form (for local files)
          files: z.array(z.instanceof(File)).optional(),
          // workflow_node_files_ids will be populated in beforeSave from uploaded files
          workflow_node_files_ids: z.array(z.number()).optional(),
          due_on_value: z
            .number({
              message: i18n.t(
                'procurement.workflows.basic_task_handler.validation.due_date_min_value'
              ),
            })
            .min(1, {
              message: i18n.t(
                'procurement.workflows.basic_task_handler.validation.due_date_min_value'
              ),
            }),
          due_on_unit: z.enum(['hours', 'days', 'weeks', 'months']),
        })
        .refine(
          (data) => {
            const hasAssignees = !!data.employee_ids?.length
            const hasSegments = !!data.employee_segments?.length
            return hasAssignees || hasSegments
          },
          {
            message: i18n.t(
              'procurement.workflows.basic_task_handler.validation.assignee_required'
            ),
            path: ['employee_ids'],
          }
        )
        .refine(
          (data) => {
            // At least one file must be selected via workflow_node_files_ids
            // files is only used temporarily before upload, workflow_node_files_ids is the source of truth
            const hasWorkflowNodeFiles =
              Array.isArray(data.workflow_node_files_ids) && data.workflow_node_files_ids.length > 0

            return hasWorkflowNodeFiles
          },
          {
            message: i18n.t(
              'procurement.workflows.download_document_handler.validation.files_required'
            ),
            path: ['workflow_node_files_ids'],
          }
        ),
    }),
  })
}

export type DownloadDocumentHandlerSchema = z.infer<
  ReturnType<typeof createDownloadDocumentHandlerSchema>
>
